import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { classificationGroupService } from "~/services/contabilidad/classificationGroupService";
import type {
  ClassificationGroupFilters,
  ClassificationGroupFormData,
} from "~/types/contabilidad";

export const CLASSIFICATION_GROUPS_QUERY_KEY = ["classificationGroups"];

export function useClassificationGroups(filters?: ClassificationGroupFilters) {
  return useQuery({
    queryKey: [...CLASSIFICATION_GROUPS_QUERY_KEY, filters],
    queryFn: () => classificationGroupService.getAll(filters),
  });
}

export function useClassificationGroupsByCompany(companyId: string) {
  return useQuery({
    queryKey: [...CLASSIFICATION_GROUPS_QUERY_KEY, "company", companyId],
    queryFn: () => classificationGroupService.getByCompany(companyId),
    enabled: !!companyId,
  });
}

export function useClassificationGroup(id: string) {
  return useQuery({
    queryKey: [...CLASSIFICATION_GROUPS_QUERY_KEY, id],
    queryFn: () => classificationGroupService.getById(id),
    enabled: !!id,
  });
}

export function useCreateClassificationGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ClassificationGroupFormData) =>
      classificationGroupService.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: CLASSIFICATION_GROUPS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...CLASSIFICATION_GROUPS_QUERY_KEY, "company", data.company_id],
      });
      toast.success("Grupo de cuenta creado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al crear grupo: ${error.message}`);
    },
  });
}

export function useUpdateClassificationGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<ClassificationGroupFormData>;
    }) => classificationGroupService.update(id, data),
    onSuccess: (updatedGroup, { id }) => {
      queryClient.invalidateQueries({ queryKey: CLASSIFICATION_GROUPS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...CLASSIFICATION_GROUPS_QUERY_KEY, id],
      });
      queryClient.invalidateQueries({
        queryKey: [...CLASSIFICATION_GROUPS_QUERY_KEY, "company", updatedGroup.company_id],
      });
      toast.success("Grupo de cuenta actualizado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar grupo: ${error.message}`);
    },
  });
}

export function useDeleteClassificationGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => classificationGroupService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLASSIFICATION_GROUPS_QUERY_KEY });
      toast.success("Grupo de cuenta eliminado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al eliminar grupo: ${error.message}`);
    },
  });
}

export function useCreateClassificationGroupsBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      groups,
    }: {
      companyId: string;
      groups: Omit<ClassificationGroupFormData, "company_id">[];
    }) => classificationGroupService.createBatch(companyId, groups),
    onSuccess: (result, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: CLASSIFICATION_GROUPS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...CLASSIFICATION_GROUPS_QUERY_KEY, "company", companyId],
      });
      toast.success(
        `Importación completada: ${result.created} creados, ${result.skipped} omitidos`
      );
    },
    onError: (error: Error) => {
      toast.error(`Error en importación: ${error.message}`);
    },
  });
}
